/**
 * Route guards.
 *
 * The client guard is a courtesy, not a security boundary: every one of these
 * checks is also made by the API, which is where it actually holds. What it
 * buys is that a member never sees a screen flash before the API says no, and
 * that a member who asks for something their file does not yet carry lands on
 * the step that would give it to them rather than on an empty dashboard.
 */

import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { ROUTES } from '@metal/shared'
import { Skeleton } from '@metal/ui'
import { useSession } from '@metal/ui/session'

export type GuardKind = 'public' | 'authenticated' | 'member' | 'seller' | 'buyer'

export function RouteGuard({
  kind,
  permission,
  children,
}: {
  kind: GuardKind
  permission?: string
  children: ReactNode
}) {
  const { user, loading } = useSession()
  const location = useLocation()

  if (kind === 'public') return <>{children}</>

  if (loading) {
    return (
      <div className="page">
        <Skeleton h={28} w="220px" />
        <Skeleton h={240} />
      </div>
    )
  }

  if (!user) {
    return <Navigate to={ROUTES.login(location.pathname + location.search)} replace />
  }

  // A page that asks only for a signed in account stays open to a half
  // finished file. The board, the analysis and the instructions are what a
  // member reads before deciding to trade at all, and the file is opened from
  // that decision rather than standing in front of it.
  if (kind === 'authenticated') return <>{children}</>

  // Anything past that needs a completed profile. A guest who reaches for a
  // trading page is taken into the complete-profile flow, which opens on
  // whatever the file still needs: the person type, the form, the contract,
  // or the status of a file already sent.
  if (user.membership !== 'active') {
    return <Navigate to={ROUTES.register()} replace />
  }

  if (kind === 'seller' && !user.sides.includes('sell')) {
    return <Navigate to={ROUTES.forbidden()} replace />
  }
  if (kind === 'buyer' && !user.sides.includes('buy')) {
    return <Navigate to={ROUTES.forbidden()} replace />
  }
  if (permission && !user.permissions.includes(permission)) {
    return <Navigate to={ROUTES.forbidden()} replace />
  }

  return <>{children}</>
}

/** A member landing on the public root goes to their dashboard. */
export function RootRedirect({ children }: { children: ReactNode }) {
  const { user, loading } = useSession()
  if (loading) return <>{children}</>
  if (user && user.membership === 'active') return <Navigate to={ROUTES.dashboard()} replace />
  return <>{children}</>
}
